import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  useMediaQuery,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { Contact } from "../../models/Contact";
import { composeMessage, MessagePart } from "../../utils/messageUtils";
import { clearMessageParts, sendNewMessage } from "../../redux/messagesSlice";
import { selectContact } from "../../redux/chatListSlice";
import { useConnection } from "../../contexts/ConnectionContext";
import { useSecurity } from "../../contexts/SecurityContext";
import {
  setSelectedChatView,
  setCurrentNavigationView,
  setSelectedWalletTab,
} from "../../redux/uiSlice";
import { getReceiversPublicKey } from "../../redux/userSlice";
import {
  fetchPolBalance,
  fetchUserBalance,
  transferTokens,
  fetchPolygonGasPrices,
} from "../../redux/ndeipiCoinSlice";
import {
  convertFromBaseToken,
  convertToBaseToken,
} from "../../utils/ndeipiTokenConversion";
import { NdeipiTokenDenomination } from "../../types/ndeipiTokenDenomination";
import { resetTransactionState } from "../../redux/ndeipiCoinSlice";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { fetchGlobalContacts } from "../../redux/contactSlice";
import { transferCollectibleAssets } from "../../redux/walletSlice";

// Mock tokens in wallet
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const SendSection = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedListContact, setSelectedListContact] =
    useState<Contact | null>(null);
  const [amount, setAmount] = useState("");
  const { chainType } = useAppSelector((state) => state.wallet);
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);

  const mockTokens = React.useMemo(
    () =>
      chainType === "solana"
        ? [{ symbol: "SOL", balance: 0.0 }]
        : [
            { symbol: "Zviri", balance: 0.0 },
            { symbol: "Vibes", balance: 0.0 },
            { symbol: "Nzou", balance: 0.0 },
            { symbol: "Shanga", balance: 0.0 },
            { symbol: "Ndeipi", balance: 0.0 },
          ],
    [chainType]
  );
  const [selectedToken, setSelectedToken] = useState(mockTokens[0]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [gasPrice, setGasPrice] = useState("50");
  const [gasLimit, setGasLimit] = useState("21000");
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [recipientAddress, setRecipientAddress] = useState<string>("");
  const { globalContacts: contacts } = useAppSelector(
    (state) => state.contacts
  );
  const { currentTransaction } = useAppSelector((state) => state.ndeipiCoin);
  const { userSelectedAmount, selectedDenomination } = useAppSelector(
    (state) => state.ui
  );
  const { selectedToken: selectedAsset } = useAppSelector(
    (state) => state.wallet
  );
  const { assetBalances } = useAppSelector((state) => state.assets);
  const theme = useTheme();
  // const { privateKey, publicKey } = useSecurity();
  // const { connection } = useConnection();
  // const { messageParts } = useAppSelector((state) => state.messages);
  // const { receiversPublicKey } = useAppSelector((state) => state.user);
  const [showTransactionStatus, setShowTransactionStatus] = useState(false);
  const dispatch = useAppDispatch();
  const { polBalance, userBalance, gasPrices } = useAppSelector(
    (state) => state.ndeipiCoin
  );

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.walletAddress?.includes(searchQuery) ||
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.emailAddress?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<string>("");
  const [customAmount, setCustomAmount] = useState<string>("");

  // Predefined amount options
  const quickAmounts = ["10", "25", "50", "100", "200"];

  // Handle quick amount selection
  const handleAmountSelect = (amount: string) => {
    setSelectedAmount(amount);
    setShowCustomAmount(false);
    setCustomAmount("");
  };

  // Handle custom amount input
  const handleCustomAmountClick = () => {
    setShowCustomAmount(true);
    setSelectedAmount("");
  };

  // Handle numeric keypad input
  const handleNumberInput = (value: string) => {
    if (value === "<") {
      // Backspace functionality
      setCustomAmount((prev) => prev.slice(0, -1));
    } else if (value === ".") {
      // Add decimal point if not already present
      if (!customAmount.includes(".")) {
        setCustomAmount((prev) => prev + value);
      }
    } else {
      // Regular number input
      setCustomAmount((prev) => {
        // Prevent multiple leading zeros
        if (prev === "0" && value !== ".") {
          return value;
        }
        return prev + value;
      });
    }
  };

  // Handle deposit action
  const hnadleBridgeSend = () => {
    const amountToDeposit = showCustomAmount ? customAmount : selectedAmount;
    if (amountToDeposit && parseFloat(amountToDeposit) > 0) {
      //Write send logic here
      //onSend(amountToDeposit);
    }
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "50vh" : "68vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  const handleClearSend = () => {
    setSearchQuery("");
    setSelectedListContact(null);
    setAmount("");
    setRecipientAddress("");
    setSelectedToken(mockTokens[0]);
    setGasPrice("50");
    setGasLimit("21000");
    dispatch(clearMessageParts());
    setShowTransactionStatus(false);
  };

  const handleSend = async () => {
    setShowTransactionStatus(true);
    if (chainType === "evm") {
      //console.log("***** Selected Asset =>", selectedAsset);
      if (selectedAsset?.id === "ndeipi") {
        const convertedAmount: string = convertToBaseToken(
          Number(amount),
          selectedToken?.symbol.toLowerCase() as NdeipiTokenDenomination
        );

        //console.log("Transferring Ndeipi tokens...");
        await dispatch(
          transferTokens({
            recipient: selectedListContact?.walletAddress || recipientAddress,
            amount: convertedAmount,
            gasPrice: gasPrice,
          })
        ).unwrap();
      } else {
        //console.log("Transferring collectible asset...");
        await dispatch(
          transferCollectibleAssets({
            recipient: selectedListContact?.walletAddress || recipientAddress,
            amount: amount,
          })
        ).unwrap();
      }

      /*
      if (transferTokens.fulfilled.match(transferResponse)) {
        console.log("Transfer successful:", transferResponse);

        //send message to recipient

        const newMessageParts: MessagePart[] = [
          ...messageParts,
          { type: "token", content: `sent*${amount}*${selectedToken.symbol}` },
        ];

        try {
          await dispatch(
            sendNewMessage({
              content: composeMessage(newMessageParts),
              connection,
              receiversPublicKey,
              privateKey,
              publicKey,
              selectedContact: selectedListContact,
              messageParts: newMessageParts,
              walletAddress: selectedListContact?.walletAddress ?? "",
            })
          ).unwrap();

          dispatch(clearMessageParts());

          if (selectedListContact) {
            dispatch(selectContact({ contact: selectedListContact }));
          }
          // Reset the form after sending
          handleClearSend();

          dispatch(setSelectedChatView("chat"));
          dispatch(setCurrentNavigationView("chat"));
        } catch (error) {
          console.error("Message send failed:", error);
        }
      }*/
    } else if (chainType === "solana") {
      console.log("Sending tokens on Solana...");
    }

    console.log({
      recipient: selectedListContact?.walletAddress || recipientAddress,
      amount,
      token: selectedToken.symbol,
      gasPrice,
      gasLimit,
    });
  };

  const handleBackToWallet = useCallback(() => {
    dispatch(resetTransactionState());
    setShowTransactionStatus(false);
    setSelectedWalletTab("main");
    // Add any navigation logic you have to return to wallet view
  }, [dispatch, setShowTransactionStatus]);

  const getSelectedTokenBalance = useCallback(() => {
    if (!selectedToken) return 0;

    if (selectedToken.symbol.toLowerCase() === "sol") {
      console.log("Returning SOL balance");
      return userBalance || "0";
    }

    return selectedAsset?.id === "ndeipi"
      ? convertFromBaseToken(
          userBalance || "0",
          selectedToken.symbol.toLowerCase() as NdeipiTokenDenomination
        )
      : assetBalances.find(
          (balance) =>
            balance.asset.symbol.toLowerCase() ===
            selectedAsset?.symbol.toLowerCase()
        )?.balance || 0;
  }, [selectedToken, userBalance, selectedAsset, assetBalances]);

  const renderTransactionStatus = () => {
    if (!showTransactionStatus) return null;

    return (
      <Box
        sx={{
          mt: 3,
          p: 3,
          border: "1px solid",
          borderColor:
            currentTransaction.status === "failed"
              ? "error.main"
              : "primary.main",
          borderRadius: 1,
          backgroundColor: "background.paper",
        }}
      >
        {currentTransaction.status === "pending" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Transaction in progress...</Typography>
            </Box>
            {currentTransaction.hash && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a
                  href={`https://${
                    chainType === "evm" ? "etherscan.io" : "solscan.io"
                  }/tx/${currentTransaction.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on block explorer
                </a>
              </Typography>
            )}
          </>
        )}

        {currentTransaction.status === "success" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: "success.main",
              }}
            >
              <CheckCircleIcon />
              <Typography>Transaction successful!</Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={handleBackToWallet}
            >
              Back to Wallet
            </Button>
          </>
        )}

        {currentTransaction.status === "failed" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: "error.main",
              }}
            >
              <ErrorIcon />
              <Typography>Transaction failed</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {currentTransaction.error}
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={handleBackToWallet}
            >
              Back to Wallet
            </Button>
          </>
        )}
      </Box>
    );
  };

  // Render the main deposit screen (first image)
  const renderMainDepositSection = () => (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: "bold", flex: 1 }}>
          Add money
        </Typography>
        <IconButton onClick={() => setShowCustomAmount(true)}>
          <MoreVertIcon />
        </IconButton>
      </Box> */}

      {/* Subtitle */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, textAlign: "center" }}
      >
        Instantly send from your wallet balance
      </Typography>

      {/* Quick Amount Selection */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
          justifyContent: "space-between",
        }}
      >
        {quickAmounts.map((amount) => (
          <Box
            key={amount}
            sx={{
              width: "calc(33.333% - 16px)",
              minWidth: "80px",
            }}
          >
            <Paper
              elevation={selectedAmount === amount ? 2 : 0}
              sx={{
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                border:
                  selectedAmount === amount
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  selectedAmount === amount
                    ? theme.palette.primary.light
                    : "background.paper",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              onClick={() => handleAmountSelect(amount)}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                ${amount}
              </Typography>
            </Paper>
          </Box>
        ))}
        <Box
          sx={{
            width: "calc(33.333% - 16px)",
            minWidth: "80px",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              textAlign: "center",
              cursor: "pointer",
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor: "transparent",
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={handleCustomAmountClick}
          >
            <Typography variant="h6" color="text.secondary">
              Other
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ mb: 3 }} />

      {/* Selected Amount Display */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Amount to send
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          ${selectedAmount || "0"}
        </Typography>
      </Box>

      {/* Deposit Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={hnadleBridgeSend}
        disabled={!selectedAmount}
        sx={{
          py: 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "1.1rem",
          fontWeight: "bold",
        }}
      >
        Send
      </Button>
    </Box>
  );

  // Render custom amount screen (second image)
  const renderCustomAmountScreen = () => (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => {
            setShowCustomAmount(false);
            setCustomAmount("");
          }}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: "bold", flex: 1 }}>
          Add money
        </Typography>
      </Box> */}

      {/* Amount Display */}
      <Box sx={{ textAlign: "center", mb: 2, mt: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
          ${customAmount || "0"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter amount
        </Typography>
      </Box>

      {/* Numeric Keypad */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          mb: 4,
        }}
      >
        {[
          { value: "1", display: "1" },
          { value: "2", display: "2" },
          { value: "3", display: "3" },
          { value: "4", display: "4" },
          { value: "5", display: "5" },
          { value: "6", display: "6" },
          { value: "7", display: "7" },
          { value: "8", display: "8" },
          { value: "9", display: "9" },
          { value: ".", display: "." },
          { value: "0", display: "0" },
          { value: "<", display: "<" },
        ].map((item) => (
          <Paper
            key={item.value}
            elevation={1}
            sx={{
              p: 2,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "background.paper",
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              "&:active": {
                backgroundColor: theme.palette.action.selected,
              },
              aspectRatio: "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => handleNumberInput(item.value)}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color:
                  item.value === "<"
                    ? theme.palette.error.main
                    : "text.primary",
              }}
            >
              {item.display}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Deposit Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={hnadleBridgeSend}
        disabled={!customAmount || parseFloat(customAmount) <= 0}
        sx={{
          py: 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "1.1rem",
          fontWeight: "bold",
        }}
      >
        Send
      </Button>
    </Box>
  );

  useEffect(() => {
    // Reset selected contact when search query changes
    if (userSelectedAmount) {
      setAmount(userSelectedAmount);
    }
    if (selectedDenomination) {
      setSelectedToken(
        mockTokens.find(
          (token) =>
            token.symbol.toLowerCase() === selectedDenomination.toLowerCase()
        ) || mockTokens[0]
      );
    }
  }, [userSelectedAmount, mockTokens, selectedDenomination]);

  useEffect(() => {
    dispatch(fetchGlobalContacts());
  }, [dispatch]);

  useEffect(() => {
    if (currentTransaction.status === "success") {
      const timer = setTimeout(() => {
        handleBackToWallet();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentTransaction.status, handleBackToWallet]);

  useEffect(() => {
    // Reset transaction state when component mounts
    dispatch(fetchUserBalance());
    dispatch(fetchPolBalance());
    dispatch(fetchPolygonGasPrices());
  }, [dispatch]);

  useEffect(() => {
    // Reset transaction state when component mounts
    setGasPrice(gasPrices?.FastGasPrice || "21");
  }, [gasPrices]);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3, ...scrollableBoxStyles }}>
      {chainType === "evm" && (
        <>
          <Typography variant="h6" gutterBottom>
            Send Assets
          </Typography>
          {/* Contact Search */}
          <Box sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Send to
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search users or enter address"
              value={displayValue}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsResultsVisible(true);
                setDisplayValue(e.target.value);
              }}
              onClick={() => {
                setDisplayValue("");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Contact List */}
            {isResultsVisible && searchQuery && (
              <List sx={{ maxHeight: 200, overflow: "auto", mt: 1 }}>
                {filteredContacts.map((contact) => (
                  <ListItem
                    key={contact.walletAddress}
                    //selected={selectedContact?.walletAddress === contact.walletAddress}
                    onClick={async () => {
                      setSelectedListContact(contact);
                      setRecipientAddress(contact?.walletAddress || "");
                      setSearchQuery(contact.name);
                      setIsResultsVisible(false);
                      setDisplayValue(
                        `${contact.name} - ${contact?.walletAddress}`
                      );
                      // Fetch public key for the selected contact
                      await dispatch(
                        getReceiversPublicKey({
                          receiverId: contact.walletAddress || "",
                        })
                      );
                    }}
                  >
                    <ListItemText
                      primary={contact.name}
                      secondary={contact.emailAddress || contact.walletAddress}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {!selectedListContact && (
              <TextField
                fullWidth
                variant="outlined"
                label="Or enter recipient address"
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          {/* Amount Input */}
          <Box sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Amount
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {selectedAsset?.id === "ndeipi"
                        ? selectedToken.symbol
                        : selectedAsset?.symbol}
                    </InputAdornment>
                  ),
                }}
              />
              {selectedAsset?.id === "ndeipi" && (
                <TextField
                  select
                  label="Token"
                  value={selectedToken.symbol}
                  onChange={(e) =>
                    setSelectedToken(
                      mockTokens.find((t) => t.symbol === e.target.value) ||
                        mockTokens[0]
                    )
                  }
                  sx={{ minWidth: 120 }}
                >
                  {mockTokens.map((token) => (
                    <MenuItem key={token.symbol} value={token.symbol}>
                      {token.symbol}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              Balance: {getSelectedTokenBalance()}{" "}
              {selectedAsset?.id === "ndeipi"
                ? selectedToken.symbol
                : selectedAsset?.symbol}{" "}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              Gas Balance: {polBalance} POL
            </Typography>
          </Box>

          {/* Advanced Options */}
          <Box sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <Typography variant="subtitle1">Advanced Options</Typography>
              <IconButton>
                {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showAdvanced}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  {gasPrices?.SafeGasPrice}
                </Typography>
                <TextField
                  fullWidth
                  label="Gas Price (Gwei)"
                  value={gasPrice}
                  onChange={(e) => setGasPrice(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Gas Limit"
                  value={gasLimit}
                  onChange={(e) => setGasLimit(e.target.value)}
                />
              </Box>
            </Collapse>
          </Box>

          {/* Send Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSend}
            disabled={
              !amount ||
              (!selectedListContact && !recipientAddress) ||
              showTransactionStatus
            }
          >
            Send{" "}
            {selectedAsset?.id === "ndeipi"
              ? selectedToken.symbol
              : selectedAsset?.symbol}
          </Button>

          {/* Transaction Summary (would appear after sending) */}
          {/* <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Transaction Summary
        </Typography>
        ...
      </Paper> */}
          {renderTransactionStatus()}
        </>
      )}

      {chainType !== "evm" && (
        <>
          <Box sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Send to
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search contacts or enter address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Contact List */}
            {searchQuery && (
              <List sx={{ maxHeight: 200, overflow: "auto", mt: 1 }}>
                {filteredContacts.map((contact) => (
                  <ListItem
                    key={contact.walletAddress}
                    //selected={selectedContact?.walletAddress === contact.walletAddress}
                    onClick={async () => {
                      setSelectedListContact(contact);
                      setRecipientAddress(contact?.walletAddress || "");
                      setSearchQuery(contact.name);

                      // Fetch public key for the selected contact
                      await dispatch(
                        getReceiversPublicKey({
                          receiverId: contact.walletAddress || "",
                        })
                      );
                    }}
                  >
                    <ListItemText
                      primary={contact.name}
                      secondary={contact.emailAddress || contact.walletAddress}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {!selectedListContact && (
              <TextField
                fullWidth
                variant="outlined"
                label="Or enter recipient address"
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          {showCustomAmount
            ? renderCustomAmountScreen()
            : renderMainDepositSection()}
        </>
      )}
    </Box>
  );
};

export default SendSection;
